/**
 * Sign-in screen — email + password via Cognito USER_PASSWORD_AUTH.
 * Handles `FORCE_CHANGE_PASSWORD` / NEW_PASSWORD_REQUIRED via a second step.
 */

import { useState, type FormEvent } from 'react';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import { useAuth } from './AuthContext';
import { NewPasswordRequiredError } from './CognitoClient';
import styles from '../AppV1.module.css';

export function LoginScreen(): JSX.Element {
  const { signInWithEmail, completeNewPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [challengeUser, setChallengeUser] = useState<CognitoUser | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const onEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email, password);
      setChallengeUser(null);
    } catch (err) {
      if (err instanceof NewPasswordRequiredError) {
        setChallengeUser(err.cognitoUser);
        setError('Choose a new password to finish sign-in.');
      } else {
        setError((err as Error).message || 'Sign-in failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onNewPasswordSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!challengeUser) return;
    setError(null);
    setSubmitting(true);
    try {
      await completeNewPassword(challengeUser, newPassword);
      setChallengeUser(null);
    } catch (err) {
      setError((err as Error).message || 'Password update failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (challengeUser) {
    return (
      <main className={styles.page}>
        <section className={styles.card}>
          <h1 className={styles.title}>Set a new password</h1>
          <p className={styles.subtitle}>Your account requires a password change before continuing.</p>

          <form className={styles.form} onSubmit={onNewPasswordSubmit}>
            <label className={styles.label} htmlFor="new-password">
              New password
            </label>
            <input
              id="new-password"
              className={styles.input}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? 'Saving…' : 'Save and continue'}
            </button>
          </form>

          {error ? (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          ) : null}
        </section>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <h1 className={styles.title}>Sign in to fitness v3</h1>
        <p className={styles.subtitle}>Amazon Cognito (user/password)</p>

        <form className={styles.form} onSubmit={onEmailSubmit}>
          <label className={styles.label} htmlFor="email">
            Email
          </label>
          <input
            id="email"
            className={styles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />

          <label className={styles.label} htmlFor="password">
            Password
          </label>
          <input
            id="password"
            className={styles.input}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            minLength={8}
          />

          <button
            type="submit"
            className={styles.primaryButton}
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {error ? (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        ) : null}
      </section>
    </main>
  );
}
